import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TurnstileInstance } from '@marsidev/react-turnstile';

import { useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { AppScrollProps } from 'components/layout/PursuitContainer';
import { TextInput } from 'components/ui/TextInput';
import { TurnstileWrapper } from 'components/ui/TurnstileWrapper';
import { Button } from 'components/ui/Button';

import { GrandLogoVert } from 'components/svg/GrandLogoVert';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import { Bumper, Divider, FullBleedScroller } from 'styles/shared';
import { AuthForm, OAuthIconWrapper } from 'styles/auth';
import { LinkedIn } from 'components/svg/LinkedIn';
import { Google } from 'components/svg/Google';
import { Provider } from '@supabase/supabase-js';

export const SignIn = ({ appScrollToTop }: AppScrollProps) => {
    const { signInWithPassword, signInWithOAuth } = useAuth();
    const { showErrorToast } = useToast();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [isSigningIn, setIsSigningIn] = useState<boolean>(false);

    const turnstileRef = useRef<TurnstileInstance>();

    useEffect(() => {
        appScrollToTop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onEmailInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setEmail(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onPasswordInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setPassword(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSigningIn) {
                return;
            }
            setIsSigningIn(true);

            if (captchaToken == null) {
                showErrorToast('CAPTCHA verification is required to sign in');
                setIsSigningIn(false);
                return;
            }

            const error = await signInWithPassword?.({
                email,
                password,
                captchaToken,
            });

            if (error != null) {
                turnstileRef.current?.reset();
                setIsError(true);
                showErrorToast(error.message);
            }

            setIsSigningIn(false);
        },
        [
            captchaToken,
            email,
            isSigningIn,
            password,
            showErrorToast,
            signInWithPassword,
        ]
    );

    const onSignInWithOAuth = useCallback(
        (provider: Provider) =>
            async (event: React.FormEvent<HTMLButtonElement>) => {
                event.preventDefault();

                if (isSigningIn) {
                    return;
                }
                setIsSigningIn(true);

                const error = await signInWithOAuth?.(provider);

                if (
                    error != null &&
                    error.message !== 'Auth session missing!'
                ) {
                    turnstileRef.current?.reset();
                    setIsError(true);
                    showErrorToast(error.message);
                }

                setIsSigningIn(false);
            },
        [isSigningIn, showErrorToast, signInWithOAuth]
    );

    return (
        <FullBleedScroller>
            <Bumper />
            <Bumper $shouldHideOnMobile />
            <div>
                <GrandLogoVert width={140} />
            </div>
            <Bumper />
            <AuthForm onSubmit={onSubmit}>
                <Fonts.Heading6 $color={colors.mountainAsh}>
                    {`Sign in`}
                </Fonts.Heading6>
                <Bumper />
                <TextInput
                    type="email"
                    id="email"
                    name="email"
                    onInput={onEmailInput}
                    label="Email address"
                    $isError={isError}
                    required
                    $backgroundColor={transparencies.darkJungle5}
                />
                <Bumper />
                <TextInput
                    type="password"
                    id="password"
                    name="password"
                    onInput={onPasswordInput}
                    label="Password"
                    $isError={isError}
                    required
                    $backgroundColor={transparencies.darkJungle5}
                />
                <Bumper />
                <TurnstileWrapper
                    turnstileRef={turnstileRef}
                    onSuccess={setCaptchaToken}
                />
                <Bumper />
                <Button
                    type="submit"
                    value="Submit"
                    $isLoading={isSigningIn}
                    $isStretch
                >
                    {`Sign in`}
                </Button>
                <Bumper />
                <Link to="/password-reset">
                    <Fonts.Small $isUnderlined>{`Reset password`}</Fonts.Small>
                </Link>
                <Bumper $minHeight={spacing.xxSmall} />
                <Divider />
                <Bumper $minHeight={spacing.xxSmall} />
                <Fonts.Small $textAlign="center">
                    {`Sign in options for The Grand Pursuit only`}
                </Fonts.Small>
                <Bumper $minHeight={spacing.large} />
                <Button
                    onClick={onSignInWithOAuth('linkedin_oidc')}
                    $isDisabled={isSigningIn}
                    $style="outline"
                    $isStretch
                >
                    <OAuthIconWrapper>
                        <LinkedIn />
                    </OAuthIconWrapper>
                    {`Continue with LinkedIn`}
                </Button>
                <Bumper />
                <Button
                    onClick={onSignInWithOAuth('google')}
                    $isDisabled={isSigningIn}
                    $style="outline"
                    $isStretch
                >
                    <OAuthIconWrapper>
                        <Google />
                    </OAuthIconWrapper>
                    {`Continue with Google`}
                </Button>
            </AuthForm>
            <Bumper $shouldHideOnMobile />
            <Bumper />
            <Bumper />
        </FullBleedScroller>
    );
};
